import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { leerePosition } from '@/types/bestellung';
import type { PositionInput } from '@/types/bestellung';
import { FOKUS_RING } from '@/lib/fokus';

type Props = {
  onAdd: (p: PositionInput) => void;
  /** data-tour-Name fuer die Einfuehrungstour. */
  anker?: string;
  // Optional kontrolliert: das Erfassungs-Sheet haelt den Feldinhalt selbst,
  // damit er in den Entwurf wandert (Befund 43). Ohne diese Props verhaelt
  // sich die Komponente wie vorher mit eigenem State.
  wert?: string;
  onWertChange?: (v: string) => void;
};

function makeUid(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

export function FreitextInput({ onAdd, anker, wert, onWertChange }: Props) {
  const [intern, setIntern] = useState('');
  const kontrolliert = wert !== undefined && onWertChange !== undefined;
  const text = kontrolliert ? wert : intern;
  const setText = kontrolliert ? onWertChange : setIntern;

  // Der Titel geht so hinaus, wie er eingegeben wurde.
  //
  // Bis hierher stand ein „[Freitext] " davor, und zwar im Titelfeld
  // selbst — in der Produktions-Datenbank etwa „[Freitext] GA 220 -
  // Antiquarisch". Der Marker gehoert dort nicht hin: Die Herkunft steht
  // schon als `ist_freitext` in der Datenbank und als Merkzeichen „von
  // Hand" an der Position, und der Titel erscheint so auch auf dem
  // gedruckten Zettel. Dasselbe stand als Begruendung schon in
  // IsbnInput.tsx, wo der Vorsatz aus demselben Grund weggelassen wird.
  function add(): void {
    const t = text.trim();
    if (t === '') return;
    onAdd({
      ...leerePosition(makeUid()),
      titel: t
    });
    setText('');
  }

  return (
    // Umbrechen statt quetschen — dieselbe Ursache wie beim ISBN-Feld
    // (K11): `flex-1` ohne `min-w-0` plus ein Knopf mit Beschriftung.
    // Gemessen ragte „Zufügen" 97 Pixel über den Rand.
    <div className="flex flex-wrap gap-2" data-tour={anker}>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        placeholder="Freitext-Position (Wunsch, Beschreibung)"
        className={`min-h-[44px] min-w-0 flex-1 basis-full rounded-md border border-line bg-cream px-3 py-2.5 text-sm ${FOKUS_RING} md:basis-auto`}
      />
      <Button
        variant="secondary"
        className="flex-1 md:flex-none"
        onClick={add}
        disabled={text.trim() === ''}
      >
        <Plus className="h-4 w-4" />
        Zufügen
      </Button>
    </div>
  );
}
